import React, { useState, useEffect, useCallback } from "react";
import { LightboxProps } from "@/types";
import styles from "./Lightbox.module.css";
import { ArrowLeft, ArrowRight, X } from "lucide-react";

const Lightbox: React.FC<LightboxProps> = ({
  images,
  isOpen,
  currentIndex,
  onClose,
  onNavigate,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          onNavigate("prev");
          break;
        case "ArrowRight":
          onNavigate("next");
          break;
        default:
          break;
      }
    },
    [isOpen, onClose, onNavigate]
  );

  // Handle click outside to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle image load
  const handleImageLoad = () => {
    setIsLoading(false);
  };

  // Handle image error
  const handleImageError = () => {
    setIsLoading(false);
  };

  // Reset loading state when image changes
  useEffect(() => {
    setIsLoading(true);
  }, [currentIndex]);

  // Add/remove keyboard event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !images || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];
  const hasNext = currentIndex < images.length - 1;
  const hasPrev = currentIndex > 0;

  return (
    <div className={styles.lightboxOverlay} onClick={handleBackdropClick}>
      <div className={styles.lightboxContainer}>
        {/* Close button */}
        <button
          className={styles.lightboxClose}
          onClick={onClose}
          aria-label="Close lightbox"
        >
          <X size={24} />
        </button>

        {/* Navigation buttons */}
        {hasPrev && (
          <button
            className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
            onClick={() => onNavigate("prev")}
            aria-label="Previous image"
          >
            <ArrowLeft size={24} />
          </button>
        )}

        {hasNext && (
          <button
            className={`${styles.lightboxNav} ${styles.lightboxNext}`}
            onClick={() => onNavigate("next")}
            aria-label="Next image"
          >
            <ArrowRight size={24} />
          </button>
        )}

        {/* Image container */}
        <div className={styles.lightboxImageContainer}>
          {isLoading && (
            <div className={styles.lightboxLoader}>
              <div className={styles.spinner}></div>
            </div>
          )}

          <img
            src={currentImage.src}
            alt={currentImage.alt || `Gallery image ${currentIndex + 1}`}
            className={styles.lightboxImage}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ opacity: isLoading ? 0 : 1 }}
          />
        </div>

        {/* Image counter */}
        <div className={styles.lightboxCounter}>
          {currentIndex + 1} / {images.length}
        </div>

        {/* Image caption */}
        {currentImage.caption && (
          <div className={styles.lightboxCaption}>{currentImage.caption}</div>
        )}
      </div>
    </div>
  );
};

export default Lightbox;
